import React, { useState } from "react"
import Paper from "../../ui/Paper"
import Input from "../../ui/Input"
import { useDispatch, useSelector } from "react-redux"
import {
  selectAuth,
  selectUpdatingUserAccount,
  updateUserAccountPassword,
} from "./slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"
import Label from "../../ui/Label"
import FormError from "../../ui/FormError"
import FormGroup from "../../ui/FormGroup"
import Button from "../../ui/Button"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import InputRow from "../../ui/InputRow"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import IconButton from "../../ui/IconButton"
import ActionsFooter from "../../ui/ActionsFooter"
import { TitleText } from "../../ui/PaperTitle"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 25vw;
  min-width: 20rem;
  max-width: 40rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

const Paragraph = styled.p`
  text-align: center;
  padding: 1rem 1rem 0 1rem;
`

const Bullet = styled.li`
  text-align: left;
  font-size: 0.8em;
  padding: 0.5rem 0;
  opacity: 0.8;
`

/**
 * The form validator
 */
const schema = yup
  .object({
    current_password: yup.string().required("current password is required"),
    password: yup.string().required("password is required"),
    confirmation: yup
      .string()
      .oneOf([yup.ref("password"), null], "passwords must match"),
  })
  .required()

/**
 * Change a users password
 *
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const ChangePassword = ({ id, handleCancel }) => {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const notMyPassword = auth.sub != id
  const [showPassword, setShowPassword] = useState(false)
  const updatingState = useSelector(selectUpdatingUserAccount)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })
  const onSubmit = (data) => {
    dispatch(updateUserAccountPassword({ ...data, id }))
      .unwrap()
      .then(() => {
        handleCancel()
      })

    // TODO: If successful, route back to login page, reset so that user
    // just has to enter new password (not ID again)
    // ...
  }

  // TODO: Select "reset password" from state, based loading / error display on it
  // ...

  return (
    <Panel>
      <h2>Change password</h2>
      {updatingState?._saveStatus === "updating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      {updatingState?._saveStatus == "rejected" && (
        <ErrorMsg
          msg={
            updatingState._errorMessage ||
            "an error occurred resetting a password"
          }
          showIcon={true}
        />
      )}
      <TitleText>The following rules apply for new passwords</TitleText>
      <Paragraph>
        <Bullet>A password length of at least 8 characters</Bullet>
        <Bullet>Inclusion of at least one upper case letter</Bullet>
        <Bullet>Inclusion of at least one lower case letter</Bullet>
        <Bullet>Inclusion of at least one number</Bullet>
        <Bullet>
          Inclusion of at least one special character (e.g. £$%&*@#)
        </Bullet>
        <Bullet>Sufficient complexity</Bullet>
      </Paragraph>
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="current_password">
              {notMyPassword ? "Your current password:" : "Current password"}
            </Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                {...register("current_password")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={(e) => {
                  setShowPassword(!showPassword)
                  e.preventDefault()
                }}
                secondary
              />
            </InputRow>
            <FormError>{errors.current_password?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="password">
              {notMyPassword ? "New password for user:" : "New password"}
            </Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                {...register("password")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={(e) => {
                  setShowPassword(!showPassword)
                  e.preventDefault()
                }}
                secondary
              />
            </InputRow>
            <FormError>{errors.password?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="ctx">Repeat new password:</Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                {...register("confirmation")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={(e) => {
                  setShowPassword(!showPassword)
                  e.preventDefault()
                }}
                secondary
              />
            </InputRow>
            <FormError>{errors.confirmation?.message}</FormError>
          </FormGroup>
          <ActionsFooter>
            {handleCancel && <Button onClick={handleCancel}>Close</Button>}
            <Button type="submit" active>
              Submit
            </Button>
          </ActionsFooter>
        </form>
      </FormContainer>
    </Panel>
  )
}

ChangePassword.propTypes = {
  id: PropTypes.string, // email of the account
  handleCancel: PropTypes.func, // Cancel add role
}
export default ChangePassword
